// src/components/customers/JobAddressCard.jsx

/**
 * JobAddressCard Component
 *
 * Displays the job-specific address as a card visually parallel to CustomerHeader.
 * Read-only — to change the job address, use the existing "Edit Job" button
 * which opens the JobModal (where address + postcode are editable fields).
 *
 * Renders directly under <CustomerHeader> in ViewCustomerRooms.jsx so the user
 * sees customer info first, then job-specific info immediately below.
 *
 * @param {Object} props - Component props
 * @param {Object} props.job - Job object. Expected fields: .address, .postcode, .date (optional)
 */
const JobAddressCard = ({ job }) => {
  if (!job) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
      {/* Job Address Header — mirrors CustomerHeader's avatar + title pattern */}
      <div className="flex items-center mb-4">
        {/* Map icon — visually distinguishes "where the job happens" from "who the customer is" */}
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
          <svg
            className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
        </div>

        <div className="min-w-0 flex-1">
          {/* h2 (not h1) — the page's h1 already belongs to the customer name in CustomerHeader */}
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
            Job Address
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            {job.date ? `Job on ${job.date}` : "Where the work will be done"}
          </p>
        </div>
      </div>

      {/* Job Address Details — same icon vocabulary as CustomerHeader (orange building, purple map pin) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Address */}
        <div className="flex items-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm text-gray-500">Address</p>
            <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
              {job.address || "Not specified"}
            </p>
          </div>
        </div>

        {/* Post Code */}
        <div className="flex items-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm text-gray-500">Post Code</p>
            <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
              {job.postcode || "Not specified"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobAddressCard;
